import {
  menu,
  type MenuConfig,
  popMenu,
  popupTargetFromElement,
} from '@blocksuite/affine-components/context-menu';
import type {
  DatabaseBlockModel,
  DatabaseViewRefBlockModel,
} from '@blocksuite/affine-model';
import { LoadingIcon } from '@blocksuite/icons/lit';
import type { EditorHost } from '@blocksuite/std';

import {
  collectAllDatabases,
  collectDatabasesFromDoc,
  collectUnloadedDocs,
  type DatabaseTargetCandidate,
  databaseTitleOf,
  docTitleOf,
  loadDocs,
} from '../utils/collect-databases.js';

const resolveDatabase = (
  host: EditorHost,
  candidate: DatabaseTargetCandidate
): DatabaseBlockModel | null => {
  const doc = host.std.workspace.getDoc(candidate.docId);
  if (!doc) return null;
  const block = doc.getStore({ id: doc.id }).getBlock(candidate.blockId)?.model;
  return block?.flavour === 'affine:database'
    ? (block as unknown as DatabaseBlockModel)
    : null;
};

const applySelection = (
  model: DatabaseViewRefBlockModel,
  candidate: DatabaseTargetCandidate,
  viewId: string
) => {
  model.store.updateBlock(model, {
    sourceDocId: candidate.docId,
    sourceBlockId: candidate.blockId,
    viewId,
  });
};

/**
 * Second level of the picker: pick one of the views of a database.
 */
const buildViewItems = (
  host: EditorHost,
  model: DatabaseViewRefBlockModel,
  candidate: DatabaseTargetCandidate
): MenuConfig[] => {
  const db = resolveDatabase(host, candidate);
  const views = db?.props.views ?? [];
  if (views.length === 0) {
    return [menu.action({ name: 'No views', select: () => {} })];
  }
  return views.map(view =>
    menu.action({
      name: view.name || 'Untitled view',
      isSelected: model.props.viewId === view.id,
      select: () => {
        applySelection(model, candidate, view.id);
      },
    })
  );
};

/**
 * First level of the picker: pick the source database (across docs).
 *
 * Unloaded docs are listed separately and loaded on demand, exactly like
 * the relation property target picker.
 */
const buildDatabaseItems = (
  host: EditorHost,
  model: DatabaseViewRefBlockModel
): MenuConfig[] => {
  const workspace = host.std.workspace;
  const candidates = collectAllDatabases(workspace);

  const reopen = (anchor: HTMLElement) =>
    popDatabaseViewRefPicker(host, model, anchor);

  const items: MenuConfig[] = candidates.map(candidate =>
    menu.subMenu({
      name: `${candidate.docTitle} / ${candidate.blockTitle}`,
      options: {
        title: { text: candidate.blockTitle },
        items: buildViewItems(host, model, candidate),
      },
    })
  );

  const unloadedDocs = collectUnloadedDocs(workspace);
  if (unloadedDocs.length > 0) {
    const loadDoc = (docId: string, anchor: HTMLElement) => {
      const doc = workspace.getDoc(docId);
      if (!doc) return;
      loadDocs([doc])
        .then(() => reopen(anchor))
        .catch(err =>
          console.warn('[orghub-database] failed to load doc', docId, err)
        );
    };
    items.push(
      menu.group({
        name: 'Not loaded yet',
        items: unloadedDocs.slice(0, 20).map(meta =>
          menu.action({
            name: `Load “${docTitleOf(workspace, meta.id)}”…`,
            prefix: LoadingIcon(),
            select: (ele: HTMLElement) => {
              loadDoc(meta.id, ele);
            },
          })
        ),
      })
    );
  }

  if (items.length === 0) {
    return [menu.action({ name: 'No databases found', select: () => {} })];
  }
  return items;
};

/**
 * Open the "which database / which view" picker for a view ref block.
 */
export const popDatabaseViewRefPicker = (
  host: EditorHost,
  model: DatabaseViewRefBlockModel,
  anchor: HTMLElement
) => {
  popMenu(popupTargetFromElement(anchor), {
    options: {
      title: { text: 'Source database' },
      items: buildDatabaseItems(host, model),
    },
  });
};

export { collectDatabasesFromDoc, databaseTitleOf };
